import { useState, useRef, useCallback } from 'react';
import { useDisclosure, useOutsideClick } from '@chakra-ui/react';
import { useMutation } from 'urql';
// Local imports
import { columnNameToColumnId, columnTypeToId } from '../utils';
import {
  ColumnType,
  EventResource,
  EventType,
  OrderDeepFragment,
  UpdateOrderDocument,
} from 'src/gql/graphql';
import { useEventHistory } from 'src/hooks/event-history';

type UseGroupPopoverProps = {
  thisOrder: OrderDeepFragment;
};

export function getAggregatedOrderGrouping(thisOrder: OrderDeepFragment) {
  if (!thisOrder.aggregatedOrderGrouping) return [];

  // For standard columns, we need to map them directly to their columnTypeToId result
  // For custom columns, we need to use the column ID
  return thisOrder.aggregatedOrderGrouping
    .map((v) => {
      if (!v.columnName || !v.columnType) return '';

      // For standard columns like ParentOrder, directly map to the ID
      if (
        v.columnType !== ColumnType.Custom &&
        v.columnType !== ColumnType.ParentOrderLabel &&
        v.columnType !== ColumnType.ItemGroup
      ) {
        // Map directly using columnTypeToId - this is more reliable for renamed columns
        return columnTypeToId(v.columnType as any);
      }

      // For custom columns, find by name and type
      const matchingColumn = thisOrder.orderColumns.find(
        (col) => col.name === v.columnName && col.columnType === v.columnType
      );

      if (matchingColumn) {
        return matchingColumn.id;
      }

      // Fall back to the old approach for backward compatibility
      return columnNameToColumnId(
        v.columnName,
        thisOrder.orderColumns,
        v.columnType
      );
    })
    .filter((v) => !!v);
}

export function useGroupPopover({ thisOrder }: UseGroupPopoverProps) {
  // The initial values should come from the database
  const [isFlatteningLoading, setIsFlatteningLoading] =
    useState<boolean>(false);
  const [grouping, setGrouping] = useState<Array<string>>(() =>
    getAggregatedOrderGrouping(thisOrder)
  );

  const [shouldFlatten, setShouldFlatten] = useState<boolean>(
    () => !!thisOrder.areItemsFlattened
  );
  const groupPopoverRef = useRef<HTMLDivElement>(null);
  const { isOpen, onClose, onOpen, onToggle } = useDisclosure();
  useOutsideClick({
    ref: groupPopoverRef,
    handler: onClose,
  });

  const [, updateOrder] = useMutation(UpdateOrderDocument);

  const { logEvent } = useEventHistory(thisOrder.projectId);

  const toggleFlattenedStatus = useCallback(async () => {
    setIsFlatteningLoading(true);
    const newState = !shouldFlatten;
    try {
      await updateOrder({
        input: {
          id: thisOrder.id,
          patch: {
            areItemsFlattened: newState,
          },
        },
      });
      setShouldFlatten(newState);
    } catch (e) {
      console.error(e);
    } finally {
      setIsFlatteningLoading(false);

      logEvent({
        eventName: newState ? 'Table Flattened' : 'Table Unflattened',
        eventType: EventType.Modified,
        resourceName: thisOrder.name,
        resourceType: EventResource.Order,
        metadata: {
          affectedResources: [thisOrder.id],
        },
      });
    }
  }, [shouldFlatten, updateOrder, thisOrder.id, thisOrder.name, logEvent]);

  return {
    groupPopoverRef,
    isGroupPopoverOpen: isOpen,
    grouping,
    shouldFlatten,
    isFlatteningLoading,
    toggleFlattenedStatus,
    setGrouping,
    onGroupPopoverOpen: onOpen,
    onGroupPopoverClose: onClose,
    onGroupPopoverToggle: onToggle,
  };
}
